import MapMesh from './MapMesh'
import Grid from './Grid'
import Building from './Building'
import DefaultTileSelector from './DefaultTileSelector'
import DefaultMapViewController from './DefaultMapViewController'
import ChunkedLazyMapMesh from './ChunkedLazyMapMesh'
import {
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Vector3,
  PCFSoftShadowMap,
  AnimationMixer,
  Mesh,
  Clock,
  Vector2,
  AmbientLight,
  DirectionalLight,
  SRGBColorSpace,
  LinearSRGBColorSpace,
  NoToneMapping,
} from 'three'
import { qrToWorld, axialToCube, roundToHex, cubeToAxial, mouseToWorld } from './coords'
import * as Util from './util'
import Sprite2D from './Sprite2D'
import SimpleModel from './SimpleModel'
import ModelFactory from './ModelFactory'

import { RendererStats } from '../../assets/vendor/renderstats'
import Stats from '../../assets/vendor/stats'

import * as MarsUtils from '../Mars/Util/util'

import { Game } from '../Core'

export default class MapView {
  constructor(canvasElementQuery = 'canvas', defaultZoom) {
    this._scrollDir = new Vector3(0, 0, 0)
    this._lastTimestamp = performance.now()
    this._zoom = 20
    this._animations = {}
    this._tileGrid = new Grid(0, 0)
    this._tileSelector = DefaultTileSelector
    this._controller = new DefaultMapViewController()
    this._animationMixer = new AnimationMixer()
    this._modelFactory = new ModelFactory()
    this.light = new AmbientLight(0x83c6ff, 0x444422)
    this.dirLight = new DirectionalLight(0xfffc9c, 1)
    this._sprites = {}
    this._clock = new Clock()
    this._mouse = new Vector2()
    this._scene = new Scene()

    let renderStats = new RendererStats()
    let stats = new Stats()

    stats.showPanel(0)

    stats.dom.style = 'position:absolute; left:0px; top: 75%'
    process.env.DEBUG_MAP && document.body.appendChild(stats.dom)

    renderStats.domElement.style.position = 'absolute'
    renderStats.domElement.style.top = '50%'
    process.env.DEBUG_MAP && document.body.appendChild(renderStats.domElement)

    this._requestAnimationId = null

    this.loop = (timestamp) => {
      this._requestAnimationId = undefined
      stats.begin()
      const dtS = (timestamp - this._lastTimestamp) / 1000.0
      const camera = this._camera
      const zoomRelative = camera.position.z / this._defaultZoom
      // Only apply scroll if scrollDir has non-zero length
      if (this._scrollDir.lengthSq() > 0) {
        const scroll = this._scrollDir
          .clone()
          .normalize()
          .multiplyScalar(this.scrollSpeed * zoomRelative * dtS)
        camera.position.add(scroll)
      }
      if (this._chunkedMesh) {
        this._chunkedMesh.updateVisibility(camera)
      }

      this._animationMixer.update(0.08)
      Object.values(this._sprites).map((sprite) => {
        sprite.update(1000 * this._clock.getDelta())
      })

      this._onAnimate(dtS)
      renderStats.update(this._renderer)
      this._renderer.render(this._scene, camera)
      this._lastTimestamp = timestamp
      stats.end()
      this.start_loop()
    }

    this.start_loop = () => {
      if (!this._requestAnimationId) {
        const newAnimationId = requestAnimationFrame(this.loop)
        this._requestAnimationId = newAnimationId
      }
    }

    this.stop = () => {
      if (this._requestAnimationId) {
        window.cancelAnimationFrame(this._requestAnimationId)
        this._animationMixer.stopAllAction()
        this._requestAnimationId = undefined
      }
    }

    this._onAnimate = () => {}
    this.scrollSpeed = 10

    this._buildingCount = 0
    const canvas = (this._canvas = document.querySelector(canvasElementQuery))
    const camera = (this._camera = new PerspectiveCamera(
      30,
      window.innerWidth / window.innerHeight,
      1,
      10000
    ))
    // Force WebGL2 context for better instanced rendering support
    console.info('Attempting to create WebGL2 context...')
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance'
    })

    if (!gl) {
      console.error('WebGL2 is not supported on this browser, falling back to WebGL1')
    } else {
      console.info('WebGL2 context created successfully')
    }

    const renderer = (this._renderer = new WebGLRenderer({
      canvas: canvas,
      context: gl,
      alpha: true,
      devicePixelRatio: window.devicePixelRatio,
    }))

    canvas.focus()

    // Verify WebGL context and extensions
    const contextType = gl instanceof WebGL2RenderingContext ? 'WebGL2' : 'WebGL1'
    console.info(`Using ${contextType} rendering context`)

    if (contextType === 'WebGL2') {
      console.info('WebGL2: Instanced rendering natively supported')
    } else {
      const hasInstancedArrays = renderer.extensions.get('ANGLE_instanced_arrays')
      if (hasInstancedArrays) {
        console.info('WebGL1: ANGLE_instanced_arrays extension available')
      } else {
        console.warn('WebGL1: ANGLE_instanced_arrays extension NOT available - rendering may fail')
      }
    }
    renderer.shadowMap.enabled = true
    renderer.shadowMap.autoUpdate = true
    renderer.shadowMap.type = PCFSoftShadowMap // default THREE.PCFShadowMap
    // Three.js v0.180.0: In r107, gammaOutput didn't affect clearColor
    // Use LinearSRGBColorSpace to prevent gamma on clearColor, but mark textures as sRGB
    renderer.setClearColor(0xdd6f2a, 0)
    renderer.setSize(window.innerWidth, window.innerHeight)
    // Three.js v0.180.0: Use LinearSRGBColorSpace to match r107 clearColor behavior
    renderer.outputColorSpace = LinearSRGBColorSpace
    // Three.js v0.180.0: Use NoToneMapping to match r107 default behavior
    renderer.toneMapping = NoToneMapping
    // Three.js v0.180.0: Use legacy lights to match r107 light intensity behavior
    renderer.useLegacyLights = true
    console.info('Renderer: NoToneMapping, useLegacyLights=true (matching r107)')
    window.addEventListener('resize', (e) => this.onWindowResize(e), false)
    // setup camera
    camera.castShadow = true
    camera.rotation.x = Math.PI / 3.5
    this.setZoom(defaultZoom)
    this._defaultZoom = defaultZoom
    this.focus(2, -4)
    // tile selector
    this._tileSelector.position.setZ(0.1)
    this._scene.add(this._tileSelector)
    this.addLight()
    this._tileSelector.visible = true
    // start rendering loop
    this.start_loop()
    this._controller.init(this, canvas)
  }

  get controller() {
    return this._controller
  }
  get canvas() {
    return this._canvas
  }
  get modelFactory() {
    return this._modelFactory
  }
  get zoom() {
    return this._zoom
  }
  get tileSelector() {
    return this._tileSelector
  }
  getZoom() {
    return this._zoom
  }
  set zoom(value) {
    this.setZoom(value)
  }
  set animations(value) {
    this._animations = value
  }
  get getAnimations() {
    return this._animations
  }
  get selectedTile() {
    return this._selectedTile
  }
  getTileGrid() {
    return this._tileGrid
  }
  get mapMesh() {
    return this._mapMesh
  }
  get getMouse() {
    return this._mouse
  }

  setMousePos(x, y) {
    this._mouse.set(x, y)
  }

  destroyListeners() {
    this._controller.destroyListeners(this._canvas)
  }
  /**
   * Sets up the camera with the given Z position (height) and so that the view center (the point the camera is pointed at) doesn't change.
   */
  setZoom(z) {
    this._camera.updateMatrixWorld(false)
    // position the camera is currently centered at
    const lookAt = this.getViewCenter()
    // move camera along the Z axis to adjust the view distance
    this._zoom = z
    this._camera.position.z = z
    this._camera.updateMatrixWorld(true)
    if (lookAt != null) {
      // reposition camera so that the view center stays the same
      this._camera.position.copy(this.getCameraFocusPositionWorld(lookAt))
    }
    return this
  }
  // ROTATE view
  rotateVector(vectorToUse, degInRad) {
    this._camera.rotateOnAxis(vectorToUse, degInRad)
  }
  //
  get scrollDir() {
    return this._scrollDir
  }
  set onTileSelected(callback) {
    this._onTileSelected = callback
  }
  set onLoaded(callback) {
    this._onLoaded = callback
  }
  set onAnimate(callback) {
    if (!callback) {
      throw new Error('Invalid onAnimate callback')
    }
    this._onAnimate = callback
  }

  addLight() {
    // Three.js v0.180.0: With LinearSRGBColorSpace output, no gamma correction is applied
    // Increase light intensities to compensate for lack of gamma correction
    this.light.intensity = 4.2 // Increased to compensate for linear output
    this.dirLight.position.set(-10, -10, 12)
    this.dirLight.intensity = 4.2 // Increased to compensate for linear output
    this.dirLight.castShadow = true
    var d = 17
    this.dirLight.shadow.mapSize.width = 512 // default 512
    this.dirLight.shadow.mapSize.height = 512 // default 512
    this.dirLight.shadow.camera.near = 0.5 // deault 0.5
    this.dirLight.shadow.camera.far = 50
    this.dirLight.shadow.camera.left = -d
    this.dirLight.shadow.camera.right = d
    this.dirLight.shadow.camera.top = d * 2
    this.dirLight.shadow.camera.bottom = -d * 2
    this.dirLight.rotation.set(0, -100, -100)
    this.dirLight.name = 'main-directional'

    // let helper = new CameraHelper(this.dirLight.shadow.camera)
    // this.add(helper)
    this._scene.add(this.light)
    this._scene.add(this.dirLight)
  }

  setOnAnimateCallback(callback) {
    this.onAnimate = callback
  }
  addCollection(collection) {
    if (collection) this._scene.add(collection)
  }

  clearAllBuildings() {
    this.removeElement(null, Building)
  }
  clearAllSprites2D() {
    this.removeElement(null, Sprite2D)
    this._sprites = {}
  }
  clearAllSimpleModels() {
    this.removeElement(null, SimpleModel)
  }

  clearAllElements() {
    this.clearAllBuildings()
    this.clearAllSprites2D()
    this.clearAllSimpleModels()
  }

  removeSprite2D(name) {
    if (this._sprites[name]) {
      delete this._sprites[name]
    }

    this.removeFromSceneByName(name)
  }

  removeElement(name, type = null) {
    if (this._scene) {
      while (
        this._scene.children.find(
          (object) => (type && object instanceof type) || object.name == name
        )
      ) {
        this._scene.children.map((object) => {
          if ((type && object instanceof type) || object.name == name) {
            this._scene.remove(object)
          }
        })
      }
    }
  }

  load(tiles, options) {
    this._tileGrid = tiles
    this._selectedTile = this._tileGrid.get(0, 0)
    this._options = options
    if (tiles.width * tiles.height < Math.pow(512, 2)) {
      if (this._mapMesh && this._scene.getObjectByName(this._mapMesh.name)) {
        this._scene.remove(this._scene.getObjectByName(this._mapMesh.name))
      }

      const mesh = (this._mapMesh = new MapMesh(tiles.toArray(), options, tiles))

      this._scene.add(this._mapMesh)
      mesh.loaded.then(() => {
        if (this._onLoaded) this._onLoaded()
      })
      console.info('using single MapMesh for ' + tiles.width * tiles.height + ' tiles')
    } else {
      const mesh = (this._mapMesh = this._chunkedMesh = new ChunkedLazyMapMesh(tiles, options))
      this._scene.add(this._mapMesh)
      mesh.loaded.then(() => {
        if (this._onLoaded) this._onLoaded()
      })
      console.info(
        'using ChunkedLazyMapMesh with ' +
          mesh.numChunks +
          ' chunks for ' +
          tiles.width * tiles.height +
          ' tiles'
      )
    }
  }
  updateTiles(tiles) {
    this._mapMesh.updateTiles(tiles)
  }
  getTile(q, r) {
    return this._mapMesh.getTile(q, r)
  }

  getSceneElementByName(name) {
    return this._scene.getObjectByName(name)
  }

  addSprite2D(id, asset, rotation, position, scale, animations, multiple = null, material = {}) {
    if (this._sprites[id]) {
      return null
    }

    let sprite = Util.handleSprite(
      id,
      asset,
      rotation,
      position,
      scale,
      animations,
      multiple,
      material
    )
    this.checkAnimation(null, sprite, sprite.getAnimations, true)
    this._sprites[id] = sprite
    this._scene.add(sprite)
  }

  removeFromSceneByName(name) {
    if (!this._scene.getObjectByName(name)) {
      return null
    }
    this._scene.remove(this._scene.getObjectByName(name))
  }

  setShadows(state) {
    this.dirLight.castShadow = state
  }

  createCustomModel(parent, parameters, models, modifications) {
    let customModels = parameters.customModels
    let canDisplay = parameters.conditions
      ? Game.evaluateAll(parameters.conditions, parent)
      : !parameters.conditions

    if (customModels && Object.keys(customModels).length > 0 && canDisplay) {
      Object.keys(customModels).map((key) => {
        let currentCustom = customModels[key]
        let modelConfig = models[key]
        let parentQR = parent.getQRPosition ? parent.getQRPosition : { q: parent.q, r: parent.r }
        let modelName = key + '_' + parentQR.q + '_' + parentQR.r

        let model = new SimpleModel(MarsUtils.asset(modelConfig.asset), this._modelFactory)
        let modelPos = new Vector3(...modelConfig.relativePosition)
        model.name = modelName

        parent.name ? (model.setParentName = parent.name) : null

        model.setCurrentAnimation(
          Util.hasAnyRunningAnimation(model, modelConfig.animations, this._animations)
        )

        if (modelConfig.modifications && modifications) {
          model.onLoadModel = (currentModel) => {
            Util.handleMeshMods(null, currentModel, modelConfig.modifications, modifications)
            this.checkAnimation(null, model, modelConfig.animations)
          }
        }

        if (currentCustom.parentPosition) {
          let parentPosition = parent.position
            ? parent.position.clone()
            : qrToWorld(parentQR.q, parentQR.r)
          parentPosition.add(modelPos)
          model.position.set(parentPosition.x, parentPosition.y, parentPosition.z)
        } else {
          model.position.set(modelPos.x, modelPos.y, modelPos.z)
        }

        if (modelConfig.rotation && modelConfig.rotation.length == 3) {
          model.rotateX(modelConfig.rotation[0])
          model.rotateY(modelConfig.rotation[1])
          model.rotateZ(modelConfig.rotation[2])
        }
        if (modelConfig.scale.length == 3) {
          model.scale.set(modelConfig.scale[0], modelConfig.scale[1], modelConfig.scale[2])
        }

        if (parameters.disableParent) {
          this.removeElement(parent.name)
        }

        this._scene.add(model)
        model.load(MarsUtils.asset(modelConfig.asset))
      })
    }
  }

  createBuilding3D(
    name,
    pos,
    asset,
    type,
    maxLevel,
    animations,
    productionAmounts,
    pricePerLevel,
    level = 1,
    built = false,
    evolving = false,
    destroying = false,
    blocked = false
  ) {
    let building = new Building(name, asset, this._modelFactory)

    building.setPosition(pos.clone())
    building.setLevel(level)
    building.setType(type)
    building.setMaxLevel(maxLevel)
    building.setProductionAmount(productionAmounts)
    building.setPricePerLevel(pricePerLevel)
    building.setAnimations(animations)
    building.setBuilt(built)
    building.setEvolving(evolving)
    building.setDestroying(destroying)
    building.setBlocked(blocked)
    building.setCurrentAnimation(
      Util.hasAnyRunningAnimation(building, animations, this._animations)
    )

    this._scene.add(building)

    building.load()

    return building
  }

  removeMountain(terrainVariants, customTile = null) {
    let tile = customTile ? customTile : this.getTile(this._selectedTile.q, this._selectedTile.r)
    let isMountain = tile.terrain == 'mountain'
    if (isMountain) {
      let copiedTile = Object.assign({}, tile)
      copiedTile.terrain = Util.varying(...terrainVariants)
      copiedTile.height = 0.5
      copiedTile.isMountain = false
      this._tileGrid.add(copiedTile.q, copiedTile.r, copiedTile)

      if (this._scene.getObjectByName(this._mapMesh.name))
        this._scene.remove(this._scene.getObjectByName(this._mapMesh.name))

      this._mapMesh = new MapMesh(this._tileGrid.toArray(), this._options)
      this._selectedTile = copiedTile
      this._scene.add(this._mapMesh)
    }
  }
  onWindowResize() {
    this._camera.aspect = window.innerWidth / window.innerHeight
    this._camera.updateProjectionMatrix()
    this._renderer.setSize(window.innerWidth, window.innerHeight)
  }
  //----- MapViewControls -----
  setScrollDir(x, y) {
    this._scrollDir.set(x, y, 0)
    if (x !== 0 || y !== 0) {
      this._scrollDir.normalize()
    }
  }
  getCamera() {
    return this._camera
  }
  /**
   * Returns the world space position on the Z plane (the plane with the tiles) at the center of the view.
   */
  getViewCenter() {
    return mouseToWorld(
      { clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 },
      this._camera
    )
  }
  getCameraFocusPosition(pos) {
    return this.getCameraFocusPositionWorld(qrToWorld(pos.q, pos.r))
  }
  getCameraFocusPositionWorld(pos) {
    const currentPos = this._camera.position.clone()
    const viewCenter = this.getViewCenter()
    const viewOffset = currentPos.sub(viewCenter)
    return pos.add(viewOffset)
  }
  focus(q, r) {
    this._camera.position.copy(this.getCameraFocusPosition({ q, r }))
  }
  focusWorldPos(v) {
    this._camera.position.copy(this.getCameraFocusPositionWorld(v))
  }
  selectTile(tile) {
    const worldPos = qrToWorld(tile.q, tile.r)
    this._tileSelector.position.set(worldPos.x, worldPos.y, 0.1)
    if (this._onTileSelected) {
      this._onTileSelected(tile)
    }
  }
  pickTile(worldPos) {
    var x = worldPos.x
    var y = worldPos.y
    // convert from world coordinates into fractal axial coordinates
    var q = (1.0 / 3) * Math.sqrt(3) * x - (1.0 / 3) * y
    var r = (2.0 / 3) * y
    // now need to round the fractal axial coords into integer axial coords for the grid lookup
    var cubePos = axialToCube(q, r)
    var roundedCubePos = roundToHex(cubePos)
    var roundedAxialPos = cubeToAxial(roundedCubePos.x, roundedCubePos.y, roundedCubePos.z)
    // just look up the coords in our grid
    var tile = this._tileGrid.get(roundedAxialPos.q, roundedAxialPos.r)
    this._selectedTile = tile
    return tile
  }

  removeAnimation(animation) {
    let action = this._animationMixer.existingAction(animation.id)
    let clip, root

    if (!action) {
      return null
    }
    clip = action.getClip()
    root = action.getRoot()
    action.uncacheAction(clip, root)
  }

  removeAllAnimations(animations) {
    animations.map((animation) => {
      this.removeAnimation(animation)
    })
  }

  checkAnimation(store, model, animations, sprite = false) {
    animations.map((animation) => {
      let currentAnimation = this._animations[animation]
      if (currentAnimation) {
        let canBePlayed =
          currentAnimation.conditions && store
            ? Game.evaluateAll(currentAnimation.conditions, store)
            : !currentAnimation.conditions
        if (sprite) {
          let clip = Util.animationClip(currentAnimation, model.name)

          let action = this._animationMixer.clipAction(clip, model)

          if (canBePlayed && !action.isRunning()) {
            if (animation.warp) action.warp(0, animation.timeScale || 1)
            if (animation.timeScale) action.setEffectiveTimeScale(animation.timeScale)

            action.play()
          } else {
            action.stop()
            this._animationMixer.uncacheAction(clip, model)
          }
        } else {
          let objectMeshes = Util.getModelsOfType(model, Mesh)
          let objectMeshIndexes = []
          objectMeshes.map((mesh, index) => objectMeshIndexes.push(index))

          let indexes = currentAnimation.ObjectsIndexes
          if (indexes[0] < 0) {
            indexes = objectMeshIndexes
          }

          indexes.map((index) => {
            let currentObject = objectMeshes[index]

            if (currentObject) {
              let clip = Util.animationClip(currentAnimation, model.name)

              let action = this._animationMixer.clipAction(clip, currentObject)

              action.isRunning() ? action.stop() : null

              if (canBePlayed) {
                model.setCurrentAnimation(clip.name)
                if (animation.warp) action.warp(0, animation.timeScale || 1)
                if (animation.timeScale) action.setEffectiveTimeScale(animation.timeScale)
                action.play()
              }
            }
          })
        }
      }
    })
  }
}
